import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { RadioField } from '@openx/components-core/lib/RadioField/RadioField';
import { packageToFormState } from '@openx/components-package/components/PackageForm/utils/formStateConverter';
import { hasContextualFees } from '@openx/components-targeting/Targeting/ContextualSegments/utils/contextualPricing';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { type DealApi, ENABLED_DATA_PARTNERS_SECTION_ID } from '@openx/types/deal';
import { type ExchangeSegmentPartnerPlatformShare, SegmentPartnerRevenueMethod } from '@openx/types/gqlTypes';
import type { PackageWithTargeting } from '@openx/types/package';
import { TargetingFields } from '@openx/types/targeting/targeting';
import { useMemo } from 'react';
import { ContextualFeesErrorAlert } from '../../components/EnableDataPartners/ContextualFeesErrorAlert';
import { REVENUE_METHOD_OPTIONS } from '../../components/EnableDataPartners/constants';
import { PricingDetailsDisplay } from '../../components/EnableDataPartners/PricingDetailsDisplay';
import { useContextualPricingState } from '../../hooks/useContextualPricingState';
import { useDealContext } from '../../utils/context/DealContext';

export type EnabledDataPartnersSectionDeal = Pick<
  DealApi,
  'account_uid' | 'segment_partner_revenue_method' | 'package_obj' | 'segment_partner_platform_share'
>;

type EnabledDataPartnersFormSectionProps = {
  segment_partner_revenue_method: SegmentPartnerRevenueMethod | null | undefined;
  segment_partner_platform_share?: Array<ExchangeSegmentPartnerPlatformShare | null> | null;
  packageObject: PackageWithTargeting | undefined;
  /** Uid of the account of the deal, which the contextual fees are priced for, as in the deal form. */
  accountUid?: string | undefined;
  isAdmin?: boolean;
};

export const EnabledDataPartnersFormSection = ({
  segment_partner_revenue_method,
  segment_partner_platform_share,
  packageObject,
  accountUid,
  isAdmin = false,
}: EnabledDataPartnersFormSectionProps) => {
  const { isOa } = useDealContext();
  const { useGetAudienceMultiproviderInfo } = useTargetingContext();

  const audienceValue = packageObject?.targeting?.audience?.openaudience_custom?.val;

  const { pricingInfo, hasMultiProviderPricingData } = useGetAudienceMultiproviderInfo({
    selectedAudienceOption: audienceValue ? 'openaudience_custom' : null,
    values: audienceValue ? [audienceValue] : undefined,
  });

  // the API mirrors `contextual_segment` into `ctx_<provider>` variables, which the converter reads
  const contextualSegments = useMemo(
    () =>
      packageObject ? packageToFormState(packageObject).targeting?.[TargetingFields.CONTEXTUAL_SEGMENTS] : undefined,
    [packageObject]
  );

  const {
    pricing: contextualPricing,
    isLoading: isContextualPricingLoading,
    isFailed: isContextualPricingFailed,
  } = useContextualPricingState({ segments: contextualSegments, accountUid });
  const hasAudienceFees = hasMultiProviderPricingData && !!pricingInfo;
  const hasContextualPricing = hasContextualFees(contextualPricing);

  // a deal with contextual fees but no saved revenue method shows the default, as the deal form does
  const revenueMethod =
    segment_partner_revenue_method ??
    (hasContextualPricing && !hasAudienceFees ? SegmentPartnerRevenueMethod.PoM : null);

  // a deal without a saved revenue method shows the section once its contextual fees load, or with their error
  if (!revenueMethod && !isContextualPricingFailed) {
    return null;
  }

  // a contextual-only deal has no fees to list when they are not priced (V3 off) or nothing is owed
  const hasContextualSegments = !!contextualSegments?.length;
  const isContextualOnlyWithoutFees =
    hasContextualSegments &&
    !audienceValue &&
    !hasContextualPricing &&
    !isContextualPricingLoading &&
    !isContextualPricingFailed;
  if (isContextualOnlyWithoutFees) {
    return null;
  }

  return (
    <FormSection
      divider
      title="Curated Deal Fees for Enabled Data Partners"
      data-test="enable-data-partners"
      rightColumnSize={RIGHT_COLUMN_WIDTH}
      id={ENABLED_DATA_PARTNERS_SECTION_ID}
    >
      {isContextualPricingLoading ? <Loader size={16} /> : null}

      {isContextualPricingFailed ? <ContextualFeesErrorAlert /> : null}

      {revenueMethod ? (
        <Typography
          data-test="oa-access-partner-heading"
          variant={'h3'}
          sx={{
            color: 'text.disabled',
          }}
        >
          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'top',
              mt: 0.5,
            }}
          >
            <RadioField
              data-test="revenue-method"
              name="segment_partner_revenue_method"
              options={REVENUE_METHOD_OPTIONS}
              row
              sx={{
                marginTop: 0.5,
              }}
              value={revenueMethod}
              isDisabled
            />
          </Stack>
        </Typography>
      ) : null}

      {isOa && !!revenueMethod && (hasAudienceFees || hasContextualPricing) && (
        <PricingDetailsDisplay
          {...(hasAudienceFees ? pricingInfo : undefined)}
          contextualPricing={contextualPricing}
          segmentPartnerRevenueMethod={revenueMethod}
          isAdmin={isAdmin}
          segmentPartnerPlatformShare={segment_partner_platform_share}
        />
      )}
    </FormSection>
  );
};
